import React from "react";
import moment from "moment";
import Immutable from "immutable";
import {Radio} from "@mui/material";
import Checkbox from "@mui/material/Checkbox";

import * as RatioRepo from "js/common/repo/ratio-repo";
import * as KpiRepo from "js/common/repo/backbone/kpi-repo";
import * as KpiCalculator from "js/common/kpi-calculator";
import * as DashboardUtils from "js/dashboards/utils";
import * as TimeframeRepo from "js/common/repo/backbone/timeframe-repo";
import * as ratioFormatter from "js/common/utils/ratio-formatter";

import ReactTooltip from "react-tooltip";
import RatioPicker from "js/common/views/inputs/ratio-picker";
import RatioPanel from "js/common/views/ratio-panel";
import CustomRatioMaker from "js/dashboards/components/ratios/custom-ratio-maker";
import DataConfigToolbar from "js/dashboards/data-config-toolbar";
import * as Overlays from "js/dashboards/components/overlays";
import RatioDetailsDialog from "js/oneview/ratio-details/dialog";
import KpiDetailsDialog from "js/oneview/kpi-details/dialog";
import {renderUserOrGroupDetails} from "js/dashboards/utils";
import {createCommonConfig} from "js/dashboards/ai-assistant/utils";
import {CustomThemeContext} from "js/common/themes/CustomThemeProvider";
import {computePriorTimeframe, isPriorPeriodComputable} from "js/dashboards/components/prior-period";
import {formatPriorPeriodVsLabel} from "js/dashboards/components/comparison-utils";
import {ComparisonPill, HigherIsBetterToggle, PriorPeriodComparisonControl} from "js/dashboards/components/comparison-components";

const RatioWithComparison = React.memo(({config, data, onClick, componentId}) => {
  const {theme} = React.useContext(CustomThemeContext);
  const valueData = data.get("value");

  const firstCurrent = valueData.current[0];
  const secondCurrent = valueData.current[1];
  const firstPrior = valueData.prior[0];
  const secondPrior = valueData.prior[1];

  const isGlobal = config.get("type") === "global";
  const ratio = isGlobal ? RatioRepo.get(config.get("id")) : null;
  const displayType = isGlobal ? ratio.get("displayType") : config.get("displayType");
  const firstKpiId = isGlobal ? ratio.get("firstKpiId") : config.get("firstKpiId");
  const secondKpiId = isGlobal ? ratio.get("secondKpiId") : config.get("secondKpiId");
  const firstKpiName = KpiRepo.get(firstKpiId).get("name");
  const secondKpiName = KpiRepo.get(secondKpiId).get("name");

  const comparison = config.get("comparison");
  const higherIsBetter = comparison.get("higherIsBetter", true);

  const clientIds = config.getIn(["clientFilter", "allClientIds"], Immutable.List());
  const hasClientFilter = clientIds.count() > 0;
  const tooltipId = `ratio-comparison-${componentId}`;

  if (hasClientFilter && (!KpiRepo.get(firstKpiId).get("filterableByClient") || !KpiRepo.get(secondKpiId).get("filterableByClient"))) {
    return (
      <div style={{padding: 10, paddingBottom: 26, cursor: "pointer", overflow: "hidden", height: "100%", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", position: "relative"}}>
        <ReactTooltip
          textColor={theme.palette.text.inverted}
          backgroundColor={theme.palette.background.inverted}
          effect="solid"
          id={tooltipId}>
          <div style={{width: 200}}>This Ratio contains a Metric that can&#39;t be filtered by client</div>
        </ReactTooltip>
        <h2 data-tip data-for={tooltipId} style={{opacity: 0.6, lineHeight: 1}}>N/A</h2>
      </div>
    );
  }

  const firstCurrentTotal = firstCurrent?.total;
  const secondCurrentTotal = secondCurrent?.total;
  const firstPriorTotal = firstPrior?.total;
  const secondPriorTotal = secondPrior?.total;

  const resolvedDisplayType = displayType || "NORMALISED";

  const formattedCurrentRatio = firstCurrentTotal && secondCurrentTotal
    ? ratioFormatter.format(resolvedDisplayType, firstCurrentTotal, secondCurrentTotal).replace(/\s/g, "")
    : null;

  const formattedPriorRatio = firstPriorTotal && secondPriorTotal
    ? ratioFormatter.format(resolvedDisplayType, firstPriorTotal, secondPriorTotal).replace(/\s/g, "")
    : null;

  const {changePct, colorSignal} = (() => {
    const none = {changePct: null, colorSignal: null};
    if (!firstCurrentTotal || !secondCurrentTotal || !firstPriorTotal || !secondPriorTotal) return none;
    const priorHasData = Math.round(firstPriorTotal.value) !== 0 && Math.round(secondPriorTotal.value) !== 0;
    if (Math.round(firstCurrentTotal.value) === 0) {
      // Current denominator is 0; if both current KPIs are zero but prior had real data, signal downward
      return (Math.round(secondCurrentTotal.value) === 0 && priorHasData)
        ? {changePct: null, colorSignal: "negative"}
        : none;
    }
    if (Math.round(secondCurrentTotal.value) === 0 && Math.round(secondPriorTotal.value) === 0) return none;
    const currentVal = secondCurrentTotal.value / firstCurrentTotal.value;
    if (Math.round(secondCurrentTotal.value) === 0) {
      return {changePct: null, colorSignal: "negative"};
    }
    if (Math.round(firstPriorTotal.value) === 0 || Math.round(secondPriorTotal.value) === 0) {
      return {changePct: null, colorSignal: currentVal > 0 ? "positive" : "negative"};
    }
    const priorVal = secondPriorTotal.value / firstPriorTotal.value;
    const pct = ((currentVal - priorVal) / Math.abs(priorVal)) * 100;
    if (isNaN(pct) || !isFinite(pct)) return none;
    return {changePct: pct, colorSignal: null};
  })();

  const effectiveChangePct = (changePct !== null && changePct !== 0 && formattedCurrentRatio !== null && formattedCurrentRatio === formattedPriorRatio)
    ? 0 : changePct;

  const isNeutral = effectiveChangePct === 0;
  const activeSignal = effectiveChangePct !== null
    ? (effectiveChangePct > 0 ? "positive" : effectiveChangePct < 0 ? "negative" : null)
    : colorSignal;
  const isGoodColor = activeSignal !== null && (activeSignal === "positive") === higherIsBetter;
  const valueColor = activeSignal === null || isNeutral
    ? theme.palette.text.main
    : (isGoodColor ? theme.palette.dashboards.ahead : theme.palette.dashboards.behind);

  const timeframeJs = config.get("timeframe").toJS();
  const timeframe = TimeframeRepo.parse(timeframeJs);
  const priorTf = computePriorTimeframe(timeframe);
  const vsLabel = formatPriorPeriodVsLabel(timeframeJs.id, timeframe.attributes.name, priorTf?.start, priorTf?.end);

  return (
    <div style={{padding: "0.5rem 1rem", cursor: "pointer", overflow: "hidden", height: "100%", display: "flex", flexDirection: "column", justifyContent: "center", position: "relative"}} onClick={onClick}>
      <div style={{display: "flex", alignItems: "center", flexWrap: "wrap", gap: 6}}>
        <span data-test-id="ratio-value" style={{fontSize: 36, fontWeight: 700, lineHeight: 1, color: valueColor, minWidth: 0}}>
          {formattedCurrentRatio ?? "—"}
        </span>
        <ComparisonPill changePct={effectiveChangePct} higherIsBetter={higherIsBetter} testId="ratio-comparison-pill"
            forceShow={formattedCurrentRatio !== null && formattedPriorRatio !== null && formattedCurrentRatio !== formattedPriorRatio} />
      </div>
      <div
          data-test-id="ratio-comparison-sublabel"
          style={{fontSize: 12, color: theme.palette.text.secondary, marginTop: 2}}>
        {vsLabel}{formattedPriorRatio ? <span style={{color: theme.palette.text.main}}>: {formattedPriorRatio}</span> : ""}
      </div>
      <div
          style={{fontSize: 11, color: theme.palette.text.main,
            position: "absolute", bottom: 8, left: 16, right: 16,
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}
          title={`${firstKpiName} / ${secondKpiName}`}>
        {firstKpiName} / {secondKpiName}
      </div>
    </div>
  );
});

const Ratio = React.memo(({
  data,
  config,
  onRequestDialog,
  onParentConfigChange,
  componentId
}) => {
  const valueData = data.get("value");
  const comparison = config.get("comparison");
  const priorComputable = isPriorPeriodComputable(TimeframeRepo.parse(config.get("timeframe").toJS()));
  const hasComparison = comparison?.get("type") === "prior_period" && priorComputable;

  if (hasComparison) {
    if (!valueData?.current?.[0] || !valueData?.current?.[1]) {
      return <Overlays.Message coverOtherContent={false}>Choose a Ratio</Overlays.Message>;
    }
    return <RatioWithComparison
        config={config}
        data={data}
        componentId={componentId}
        onClick={() => renderDialog(config, valueData.current, onParentConfigChange, onRequestDialog)} />;
  }

  if (!valueData?.[0] || !valueData?.[1]) {
    return <Overlays.Message coverOtherContent={false}>Choose a Ratio</Overlays.Message>;
  }
  return <RatioPanel
      config={config}
      componentId={componentId}
      ratioData={Immutable.Map({
        firstKpiData: Immutable.Map(valueData[0]),
        secondKpiData: Immutable.Map(valueData[1])
      })}
      clientIds={config.getIn(["clientFilter", "allClientIds"], Immutable.List())}
      currentTimeframe={TimeframeRepo.parse(config.get("timeframe").toJS())}
      onClick={() => renderDialog(config, valueData, onParentConfigChange, onRequestDialog)}
  />;
});

const RatioEditor = React.memo(({
  component,
  onComponentChange
}) => {
  const config = component.get("dataConfig");
  const {theme} = React.useContext(CustomThemeContext);

  const onConfigChange = React.useCallback(newDataConfig => {
    onComponentChange(component.set("dataConfig", newDataConfig));
  }, [onComponentChange, component]);

  const [newRatioSelected, setNewRatioSelected] = React.useState(config.get("type") === "custom");

  const kpis = React.useMemo(
      () => Immutable.fromJS(KpiRepo
          .getAll().toJSON())
          .filter(k => k.get("visible") || k.get("id") === config.get("firstKpiId")),
      [config]);

  const comparison = config.get("comparison");
  const hasPriorComparison = comparison?.get("type") === "prior_period";
  const priorComputable = isPriorPeriodComputable(TimeframeRepo.parse(config.get("timeframe").toJS()));

  return <div style={{padding: "0.5rem"}}>
    <DataConfigToolbar config={config} onConfigChange={onConfigChange} />

    <div style={{marginTop: "0.5rem"}}>
      <div style={{display: "flex", alignItems: "center"}}>
        <Radio
            size="small"
            checked={!newRatioSelected} onChange={() => {
          onConfigChange(config.set("type", "global"));
          setNewRatioSelected(!newRatioSelected);
        }} />
        <h3 style={{fontSize: 14}}>Choose an existing ratio</h3>
      </div>
      <div
          data-test-id="ratio-picker"
          style={{marginLeft: 38, width: 300, display: newRatioSelected ? "none" : "block"}}>
        <RatioPicker
            label={(config.get("id") && RatioRepo.get(config.get("id")).get("name")) || "Select Ratio"}
            ratios={RatioRepo.getAll().toList().filter(r => !r.get("isDeleted"))}
            onRatioSelect={(r) => {
              onConfigChange(
                  config
                      .set("title", r.get("name"))
                      .set("id", r.get("id"))
                      .set("firstKpiId", r.get("firstKpiId"))
                      .set("secondKpiId", r.get("secondKpiId"))
                      .set("displayType", r.get("displayType"))
                      .set("type", "global")
              );
            }}
            closeOnSelect={true} />
      </div>
    </div>
    <div>
      <div style={{display: "flex", alignItems: "center"}}>
        <span data-test-id="new-ratio-button">
          <Radio
              size="small"
              checked={newRatioSelected}
              onChange={() => {
                onConfigChange(config.delete("id").set("type", "custom"));
                setNewRatioSelected(!newRatioSelected);
              }} />
        </span>
        <h3 style={{fontSize: 14}}>Configure a custom ratio</h3>
      </div>
      <div style={{marginLeft: 38, display: newRatioSelected ? "block" : "none"}}>
        <CustomRatioMaker kpis={kpis} onConfigChange={onConfigChange} config={config} />
      </div>
    </div>
    <div style={{marginTop: 20}}>
      <PriorPeriodComparisonControl
          control={<Checkbox size="small" checked={hasPriorComparison} onChange={e => {
            const currentHeight = component.getIn(["layout", "height"], 4);
            if (e.target.checked) {
              onComponentChange(component
                  .set("dataConfig", config.set("comparison", Immutable.fromJS({type: "prior_period", higherIsBetter: true})))
                  .update("layout", layout => currentHeight <= 4 ? layout.set("height", 5) : layout));
            } else {
              onComponentChange(component
                  .set("dataConfig", config.delete("comparison"))
                  .update("layout", layout => currentHeight === 5 ? layout.set("height", 4) : layout));
            }
          }} />}
          priorComputable={priorComputable}
          testId="ratio-compare-prior-period"
          sx={{marginLeft: 0}} />
    </div>
    {hasPriorComparison && <div style={{marginTop: "0.25rem", marginLeft: "11px"}}>
      <div style={{fontSize: 11, color: theme.palette.text.secondary, marginBottom: 4}}>Higher number is</div>
      <HigherIsBetterToggle
          value={comparison.get("higherIsBetter", true)}
          onChange={val => onConfigChange(config.set("comparison", comparison.set("higherIsBetter", val)))}
          testId="ratio-higher-is-better" />
    </div>}
  </div>;
});

const renderDialog = (config, currentKpiPair, onParentConfigChange, onRequestDialog) => {
  const kpiDetailsOptions = {
    timeframe: TimeframeRepo.parse(config.get("timeframe").toJS()),
    clientIds: config.getIn(["clientFilter", "allClientIds"], Immutable.List()),
    entityToIds: (config.get("entityToIds") ?? Immutable.Map()).toObject(),
    ...DashboardUtils.configToQualifierAjaxParams(config)
  };

  const globalRatio = RatioRepo.get(config.get("id"));
  const isGlobalRatio = config.get("type") === "global";
  const name = isGlobalRatio ? globalRatio.get("name") : config.get("name");
  const firstKpiId = isGlobalRatio ? globalRatio.get("firstKpiId") : config.get("firstKpiId");
  const secondKpiId = isGlobalRatio ? globalRatio.get("secondKpiId") : config.get("secondKpiId");
  const displayType = isGlobalRatio ? globalRatio.get("displayType") : config.get("displayType");
  const ratioDetailsOptions = {
    initialTab: "DETAILS",
    ratio: Immutable.Map({
      name,
      firstKpiId,
      secondKpiId,
      displayType
    }),
    ratioData: Immutable.Map({
      firstKpiData: Immutable.Map(Immutable.fromJS(currentKpiPair[0])),
      secondKpiData: Immutable.Map(Immutable.fromJS(currentKpiPair[1]))
    }),
    timeframe: TimeframeRepo.parse(config.get("timeframe").toJS()),
    clientIds: config.getIn(["clientFilter", "allClientIds"], Immutable.List()),
    ...DashboardUtils.configToQualifierAjaxParams(config)
  };

  const kpiDialog = (kpiId) => {
    const kpiName = KpiRepo.get(kpiId).get("name");
    const kpiDialogTitle = (
        <div style={{display: "inline-block"}}>
          <span
              style={{
                fontSize: 20,
                textTransform: "none",
                fontWeight: 500
              }}>
              {kpiName}
          </span>
          <span style={{fontWeight: 500}}>
          {` (${TimeframeRepo.parse(config.get("timeframe").toJS()).attributes.name})`}
          </span>
          {renderUserOrGroupDetails(config)}
        </div>
    );
    const kpiDialogRef = React.createRef();
    const handleKpiDialogRequestClose = () => closeDialog();
    const handleDialogRequestClose = () => kpiDialogRef.current.handleCloseRequest();
    const closeDialog = onRequestDialog(
        <KpiDetailsDialog
            ref={kpiDialogRef}
            renderContentOnly
            kpiId={kpiId}
            {...kpiDetailsOptions}
            matchAnyTagIds={config.getIn(["tagFilter", "matchAnyTagIds"], Immutable.List())}
            matchAllTagIds={config.getIn(["tagFilter", "matchAllTagIds"], Immutable.List())}
            excludedTagIds={config.getIn(["tagFilter", "excludedTagIds"], Immutable.List())}
            onRequestClose={handleKpiDialogRequestClose}
            onGroupClick={groupId => {
              onParentConfigChange(parentConfig => parentConfig.set("qualifier", Immutable.Map({
                type: "GROUP",
                id: groupId
              })), true);
              closeDialog();
            }}
            onUserClick={userId => {
              onParentConfigChange(parentConfig => parentConfig.set("qualifier", Immutable.Map({
                type: "USER",
                id: userId
              })), true);
              closeDialog();
            }} />,
        {title: kpiDialogTitle, onRequestClose: handleDialogRequestClose});
  };

  // name will not be set for a custom ratio since we don't ask the user to name them.
  // If you've come looking for why custom ratio clickthroughs doesn't have full titles, this is why.
  // The title would need to be taken from the component title which is not currently available at this level.
  const ratioDialogTitle = (
      <div style={{display: "inline-block"}}>
        <span
            style={{
              fontSize: 20,
              textTransform: "none",
              fontWeight: 500
            }}>
            {name}
        </span>
        <span style={{fontWeight: 500}}>
          {` (${TimeframeRepo.parse(config.get("timeframe").toJS()).attributes.name})`}
        </span>
        {renderUserOrGroupDetails(config)}
      </div>
  );
  const closeDialog = onRequestDialog(
      <RatioDetailsDialog
          renderContentOnly
          {...ratioDetailsOptions}
          matchAnyTagIds={config.getIn(["tagFilter", "matchAnyTagIds"], Immutable.List())}
          matchAllTagIds={config.getIn(["tagFilter", "matchAllTagIds"], Immutable.List())}
          excludedTagIds={config.getIn(["tagFilter", "excludedTagIds"], Immutable.List())}
          onKpiClick={kpiId => {
            closeDialog();
            kpiDialog(kpiId);
          }}
      />,
      {title: ratioDialogTitle, minWidth: "fit-content", minHeight: 260});
};
const transformRatioConfig = (backendConfig, dashboardConfig) => {
  const ratioId = backendConfig.get("ratioIds")?.get(0);
  const kpiIds = backendConfig.get("kpiIds");

  let type = "global";
  if(!ratioId && kpiIds) {
    type = "custom";
  }

  const commonConfig = createCommonConfig(backendConfig, dashboardConfig);

  const configToMerge = {
    id: ratioId,
    type: type
  };

  if(type === "custom") {
    configToMerge.firstKpiId = backendConfig.get("kpiIds").first();
    configToMerge.secondKpiId = backendConfig.get("kpiIds").last();
    configToMerge.displayType = "NORMALISED";
  }

  const comparison = backendConfig.get("comparison");
  if (comparison) { configToMerge.comparison = comparison; }

  return commonConfig.merge(configToMerge);
};

export default Immutable.fromJS({
  type: "Ratio",
  label: "Ratio",
  titleIsEditable: true,

  previewDimensions: Immutable.Map({
    width: 200,
    height: 100
  }),
  expandedPreviewDimensions: Immutable.Map({
    width: 400,
    height: 130
  }),

  transformConfig: (backendConfig, dashboardConfig) =>
      transformRatioConfig(backendConfig, dashboardConfig),

  transformData: (data) => {
    return Immutable.Map({
      value: data
    });
  },

  getTitle: component => {
    const config = component.get("dataConfig");
    let firstKpiId;
    let secondKpiId;
    let name;
    const ratioId = config.get("id");
    const ratio = RatioRepo.get(ratioId);

    if (config.get("type") === "global") {
      firstKpiId = ratio.get("firstKpiId");
      secondKpiId = ratio.get("secondKpiId");
      name = ratio.get("name");
    } else {
      firstKpiId = config.get("firstKpiId");
      secondKpiId = config.get("secondKpiId");
    }
    if (firstKpiId && secondKpiId) {
      const firstKpiName = KpiRepo.get(firstKpiId).get("name");
      const secondKpiName = KpiRepo.get(secondKpiId).get("name");
      if (component.get("title") || component.get("title") === "") {
        return component.get("title");
      } else if (name) {
        return name;
      } else {
        return `${firstKpiName} / ${secondKpiName}`;
      }
    } else {
      return "New Ratio";
    }
  },
  getDefaultData: () => Immutable.Map(),
  getReactComponent: () => Ratio,
  getEditorReactComponent: () => RatioEditor,
  canFullScreen: false,
  variant: "standard",
  layout: {
    min: {width: 6, height: 4},
    max: {width: 16, height: 16},
    init: {width: 6, height: 4}
  },
  applyParentConfig: (parentConfig, dataConfig) => DashboardUtils.applyInheritanceForKeys(parentConfig, dataConfig),
  load: component => {
    const config = component.get("dataConfig");

    let firstKpiId;
    let secondKpiId;

    if (config.get("type") === "global") {
      const ratioId = config.get("id");
      const ratio = RatioRepo.get(ratioId);

      firstKpiId = ratio.get("firstKpiId");
      secondKpiId = ratio.get("secondKpiId");
    } else {
      firstKpiId = config.get("firstKpiId");
      secondKpiId = config.get("secondKpiId");
    }

    if (firstKpiId && secondKpiId) {
      // if filters are set don't update the ratios
      const ajaxParams = {
        dateFromUI: moment().format("YYYY-MM-DD"),
        timeframe: TimeframeRepo.parse(config.get("timeframe").toJS()),
        clientIds: config.getIn(["clientFilter", "allClientIds"]).toArray(),
        matchAnyTagIds: config.getIn(["tagFilter", "matchAnyTagIds"]).toArray(),
        matchAllTagIds: config.getIn(["tagFilter", "matchAllTagIds"]).toArray(),
        excludedTagIds: config.getIn(["tagFilter", "excludedTagIds"]).toArray(),
        entityToIds: (config.get("entityToIds") ?? Immutable.Map()).toObject(),
        ...DashboardUtils.configToQualifierAjaxParams(config),
        alwaysAllowClientFilter: true
      };

      const comparison = config.get("comparison");
      if (comparison && comparison.get("type") === "prior_period") {
        const priorTimeframe = computePriorTimeframe(ajaxParams.timeframe);
        if (priorTimeframe) {
          const priorParams = {...ajaxParams, timeframe: TimeframeRepo.parseCustom(priorTimeframe)};
          return Promise.all([
            KpiCalculator.summary(firstKpiId, ajaxParams),
            KpiCalculator.summary(secondKpiId, ajaxParams),
            KpiCalculator.summary(firstKpiId, priorParams),
            KpiCalculator.summary(secondKpiId, priorParams)
          ]).then(([firstCurrent, secondCurrent, firstPrior, secondPrior]) => ({
            current: [firstCurrent, secondCurrent],
            prior: [firstPrior, secondPrior]
          }));
        }
      }

      return Promise
          .all([
            KpiCalculator.summary(firstKpiId, ajaxParams),
            KpiCalculator.summary(secondKpiId, ajaxParams)
          ])
          .then(results => Promise.resolve(results));

    } else {
      return Promise.resolve(Immutable.Map());
    }
  }
});
